/**
 * Leaflet-based map component for showing location pings.
 *
 * Two modes:
 *   - "trail":  one agent's recent points connected as a polyline (newest = cyan).
 *   - "live":   one marker per agent at their latest known position.
 *
 * Tiles come from OpenStreetMap (free, no key needed). If you ever swap to a
 * paid tile provider (Mapbox / Stadia), only the `TileLayer.url` prop needs to
 * change.
 */
import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// React-Leaflet doesn't ship default marker icons that resolve through Webpack;
// patch the default-icon URLs to point at the CDN so markers render reliably
// regardless of bundler config.
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const cyanIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'leaflet-cyan-marker',
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 14, { animate: true });
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, points]);
  return null;
}

function fmtTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/**
 * Trail map for a single agent.
 * @param {Array<{latitude, longitude, accuracy?, timestamp}>} points  oldest → newest
 * @param {string} height                                              CSS height (default 400px)
 */
export function PingTrailMap({ points = [], height = '400px' }) {
  const valid = useMemo(
    () => points.filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude)),
    [points],
  );
  if (valid.length === 0) {
    return (
      <div
        className="rounded-xl border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-sm text-slate-400"
        style={{ height }}
      >
        No location data to map.
      </div>
    );
  }
  const latlngs = valid.map((p) => [p.latitude, p.longitude]);
  const newest = valid[valid.length - 1];

  return (
    <div className="rounded-xl overflow-hidden border border-slate-200" style={{ height }} data-testid="ping-trail-map">
      <MapContainer center={[newest.latitude, newest.longitude]} zoom={14} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={latlngs} />
        {/* Connecting line for the trail */}
        {latlngs.length > 1 && (
          <Polyline positions={latlngs} pathOptions={{ color: '#06b6d4', weight: 3, opacity: 0.75 }} />
        )}
        {valid.map((p, i) => (
          <Marker key={`${p.timestamp}-${i}`} position={[p.latitude, p.longitude]} icon={cyanIcon}>
            <Popup>
              <div className="text-xs space-y-0.5">
                <div className="font-semibold">
                  {i === valid.length - 1 ? 'Most recent ping' : `Ping #${valid.length - i}`}
                </div>
                <div>{p.latitude?.toFixed(5)}, {p.longitude?.toFixed(5)}</div>
                {p.accuracy ? <div className="text-slate-500">±{Math.round(p.accuracy)}m</div> : null}
                <div className="text-slate-500">{fmtTime(p.timestamp)}</div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

/**
 * Live map showing every agent at their latest location.
 * @param {Array<{user_id, name, role, latitude, longitude, timestamp, accuracy?}>} agents
 */
export function LiveAgentsMap({ agents = [], height = '70vh' }) {
  const valid = useMemo(
    () => agents.filter((a) => Number.isFinite(a.latitude) && Number.isFinite(a.longitude)),
    [agents],
  );
  if (valid.length === 0) {
    return (
      <div
        className="rounded-xl border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-sm text-slate-400"
        style={{ height }}
      >
        No active agents with location data.
      </div>
    );
  }
  const latlngs = valid.map((a) => [a.latitude, a.longitude]);
  const center = latlngs[0];

  return (
    <div className="rounded-xl overflow-hidden border border-slate-200" style={{ height }} data-testid="live-agents-map">
      <MapContainer center={center} zoom={11} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={latlngs} />
        {valid.map((a) => (
          <Marker key={a.user_id} position={[a.latitude, a.longitude]} icon={cyanIcon}>
            <Popup>
              <div className="text-xs space-y-0.5">
                <div className="font-semibold">{a.name}</div>
                {a.role && <div className="capitalize text-slate-500">{a.role}{a.tier ? ` · Tier ${a.tier}` : ''}</div>}
                <div>{a.latitude?.toFixed(5)}, {a.longitude?.toFixed(5)}</div>
                {a.accuracy ? <div className="text-slate-500">±{Math.round(a.accuracy)}m</div> : null}
                <div className="text-slate-500">{fmtTime(a.timestamp)}</div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
